/**
 * Registry — katalog modulu, peers, publish/install
 */
import type { ComponentType } from 'react';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';
import { callWidgetTool } from '../../../widgets/scope';
import { DEFAULT_API_TLS_PORT } from '../../../lib/node-ports';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Modal,
  RefreshButton,
  StatCard,
  StatsGrid,
  StatusDot,
} from '../../../components/ui';
import { MaturityBadge } from '../../../components/ui/MaturityBadge';
import {
  Download,
  Globe,
  Loader2,
  Package,
  Plus,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import { themedConfirm } from '../../../lib/themed-dialogs';


let _widgetScope: EffectiveScope = { node: null, identity: null };
export function setWidgetScope(scope: EffectiveScope) {
  _widgetScope = scope;
}
function callMcp<T = unknown>(
  name: string,
  args: Record<string, unknown> = {},
  opts?: { silent?: boolean; label?: string },
) {
  return callWidgetTool(name, args, _widgetScope, opts) as Promise<T>;
}

// ── Types ──────────────────────────────────────────────────────────────────

interface CatalogEntry {
  name: string;
  version: string;
  maturity: string;
  description: string;
  author: string;
  source: string;
  size_bytes?: number | null;
  published_at?: string;
  tags?: string[];
}

interface Peer {
  id: string;
  name: string;
  url: string;
  role: string;
  enabled: boolean;
  last_sync: string | null;
  sync_error: string | null;
}

// ── Catalog Tab ────────────────────────────────────────────────────────────

function CatalogTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['registry-catalog', search],
    queryFn: () =>
      callMcp<{
        ok: boolean;
        modules?: CatalogEntry[];
      }>(
        'registry.catalog',
        search.trim()
          ? { action: 'search', query: search.trim() }
          : { action: 'list' },
        { silent: true },
      ),
    staleTime: 30_000,
  });

  const entries = Array.isArray(data?.modules) ? data.modules : [];

  const installMut = useMutation({
    mutationFn: (args: { name: string; version?: string }) =>
      callMcp('registry.sync', {
        action: 'install',
        name: args.name,
        ...(args.version ? { version: args.version } : {}),
      }),
    onSuccess: () => {
      toast.success(t('registry.moduleInstalledToast'));
      qc.invalidateQueries({ queryKey: ['registry-catalog'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (args: { name: string; version?: string }) =>
      callMcp('registry.catalog', {
        action: 'delete',
        name: args.name,
        ...(args.version ? { version: args.version } : {}),
      }),
    onSuccess: () => {
      toast.success(t('registry.deletedToast'));
      refetch();
    },
  });

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2'>
        <div className='flex-1 flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2'>
          <Search size={14} className='text-gray-600' />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('registry.searchModulePlaceholder')}
            className='flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none'
          />
        </div>
        <RefreshButton onRefresh={() => refetch()} loading={isLoading} size='md' />
      </div>

      {isLoading
        ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='animate-spin text-gray-600' />
          </div>
        )
        : entries.length === 0
        ? (
          <EmptyState
            icon={<Package size={24} />}
            title={t('registry.catalogEmptyTitle')}
            description={search ? t('registry.nothingFoundDescription') : t('registry.noModulesDescription')}
          />
        )
        : (
          <div className='space-y-2'>
            {entries.map((e) => (
              <Card key={`${e.name}-${e.version}`}>
                <CardContent className='py-3'>
                  <div className='flex items-center gap-3'>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2'>
                        <span className='text-sm font-semibold text-white'>{e.name}</span>
                        <span className='text-[10px] text-gray-600 font-mono'>v{e.version}</span>
                        <MaturityBadge maturity={e.maturity} showStable />
                        <Badge variant='secondary'>{e.source}</Badge>
                      </div>
                      <p className='text-xs text-gray-500 mt-0.5 truncate'>{e.description}</p>
                      <div className='flex gap-3 mt-1 text-[10px] text-gray-600'>
                        <span>{e.author}</span>
                        {e.size_bytes && <span>{(e.size_bytes / 1024).toFixed(0)} KB</span>}
                        {e.published_at && (
                          <span>{new Date(e.published_at).toLocaleDateString('cs')}</span>
                        )}
                        {(e.tags?.length ?? 0) > 0 && <span>{e.tags.join(', ')}</span>}
                      </div>
                    </div>
                    <div className='flex gap-1'>
                      <Button
                        variant='primary'
                        size='sm'
                        onClick={() => installMut.mutate({ name: e.name, version: e.version })}
                        loading={installMut.isPending}
                        icon={<Download size={12} />}
                      >
                        {t('registry.installButton')}
                      </Button>
                      <Button
                        variant='danger'
                        size='sm'
                        onClick={async () => {
                          if (await themedConfirm(t('registry.confirmDeleteEntry', { name: e.name, version: e.version }))) {
                            deleteMut.mutate({
                              name: e.name,
                              version: e.version,
                            });
                          }
                        }}
                        icon={<Trash2 size={12} />}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}

// ── Peers Tab ──────────────────────────────────────────────────────────────

function PeersTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['registry-peers'],
    queryFn: () =>
      callMcp<{ ok: boolean; peers: Peer[] }>('registry.peers', { action: 'list' }, { silent: true }),
    staleTime: 30_000,
  });

  const peers = Array.isArray(data?.peers) ? data.peers : [];

  const addMut = useMutation({
    // MCP registry.peers add nepřijímá/neukládá api_key (mTLS identity)
    mutationFn: () => callMcp('registry.peers', { action: 'add', name, url }),
    onSuccess: () => {
      toast.success(t('registry.peerAddedToast'));
      setAddOpen(false);
      setName('');
      setUrl('');
      setApiKey('');
      refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleMut = useMutation({
    mutationFn: (p: { name: string; enabled: boolean }) =>
      callMcp('registry.peers', {
        action: p.enabled ? 'enable' : 'disable',
        name: p.name,
      }),
    onSuccess: () => refetch(),
  });

  const removeMut = useMutation({
    mutationFn: (peerName: string) =>
      callMcp('registry.peers', { action: 'remove', name: peerName }),
    onSuccess: () => {
      toast.success(t('registry.peerRemovedToast'));
      refetch();
    },
  });

  const syncMut = useMutation({
    mutationFn: (args: { action: string; peer?: string }) =>
      callMcp('registry.sync', {
        action: args.action,
        ...(args.peer ? { peer: args.peer } : {}),
      }),
    onSuccess: (_, vars) => {
      toast.success(t('registry.syncDoneToast', { action: vars.action }));
      qc.invalidateQueries({ queryKey: ['registry-catalog'] });
    },
  });

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='flex gap-2'>
          <Button
            variant='primary'
            size='sm'
            onClick={() => setAddOpen(true)}
            icon={<Plus size={14} />}
          >
            {t('registry.addPeerTitle')}
          </Button>
          <Button
            variant='secondary'
            size='sm'
            onClick={() => syncMut.mutate({ action: 'pull' })}
            loading={syncMut.isPending}
            icon={<Download size={14} />}
          >
            {t('registry.pullButton')}
          </Button>
          <Button
            variant='secondary'
            size='sm'
            onClick={() => syncMut.mutate({ action: 'push' })}
            loading={syncMut.isPending}
            icon={<Upload size={14} />}
          >
            {t('registry.pushButton')}
          </Button>
        </div>
        <RefreshButton onRefresh={() => refetch()} loading={isLoading} size='md' />
      </div>

      {peers.length === 0
        ? (
          <EmptyState
            icon={<Globe size={24} />}
            title={t('registry.noPeersTitle')}
            description={t('registry.noPeersDescription')}
          />
        )
        : (
          <div className='space-y-2'>
            {peers.map((p) => (
              <Card key={p.name}>
                <CardContent className='py-3'>
                  <div className='flex items-center gap-3'>
                    <StatusDot status={p.enabled ? 'running' : 'stopped'} />
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2'>
                        <span className='text-sm font-semibold text-white'>{p.name}</span>
                        <Badge>{p.role}</Badge>
                      </div>
                      <span className='text-xs text-gray-500 font-mono'>{p.url}</span>
                      {p.sync_error && (
                        <p className='text-[10px] text-red-400 mt-0.5'>{p.sync_error}</p>
                      )}
                      {p.last_sync && (
                        <span className='text-[10px] text-gray-600 ml-2'>
                          {t('registry.lastSyncLabel', { date: new Date(p.last_sync).toLocaleString('cs') })}
                        </span>
                      )}
                    </div>
                    <div className='flex gap-1'>
                      <Button
                        variant={p.enabled ? 'warning' : 'success'}
                        size='sm'
                        onClick={() => toggleMut.mutate({ name: p.name, enabled: !p.enabled })}
                      >
                        {p.enabled ? t('registry.deactivateButton') : t('registry.activateButton')}
                      </Button>
                      <Button
                        variant='danger'
                        size='sm'
                        onClick={async () => {
                          if (await themedConfirm(t('registry.confirmRemovePeer', { name: p.name }))) removeMut.mutate(p.name);
                        }}
                        icon={<Trash2 size={12} />}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title={t('registry.addPeerTitle')}>
        <div className='space-y-3'>
          <div>
            <span className='text-[11px] text-gray-500'>{t('registry.nameLabel')}</span>
            <input
              className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500/50'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='acm-node2'
            />
          </div>
          <div>
            <span className='text-[11px] text-gray-500'>{t('registry.urlLabel')}</span>
            <input
              className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500/50'
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={`https://10.x.x.x:${DEFAULT_API_TLS_PORT}/apiext/registry`}
            />
          </div>
          <div>
            <span className='text-[11px] text-gray-500'>{t('registry.apiKeyLabel')}</span>
            <input
              className='mt-0.5 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500/50'
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder='adx_...'
            />
          </div>
          <div className='flex justify-end gap-2 pt-2'>
            <Button variant='ghost' onClick={() => setAddOpen(false)}>{t('common.cancel')}</Button>
            <Button variant='primary' onClick={() => addMut.mutate()} loading={addMut.isPending}>
              {t('registry.addButton')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Publish Tab ────────────────────────────────────────────────────────────

function PublishTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [moduleName, setModuleName] = useState('');

  const publishMut = useMutation({
    mutationFn: (name: string) =>
      callMcp('registry.publish', { action: 'from_local', name }),
    onSuccess: () => {
      toast.success(t('registry.modulePublishedToast'));
      qc.invalidateQueries({ queryKey: ['registry-catalog'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Health dříve GET /apiext/registry/health — skládáme z catalog + peers list
  const { data: healthData } = useQuery({
    queryKey: ['registry-health'],
    queryFn: async () => {
      const [cat, peers] = await Promise.all([
        callMcp<{ ok?: boolean; total?: number; modules?: unknown[] }>(
          'registry.catalog',
          { action: 'list' },
          { silent: true },
        ),
        callMcp<{ ok?: boolean; peers?: Peer[] }>(
          'registry.peers',
          { action: 'list' },
          { silent: true },
        ),
      ]);
      return {
        ok: true,
        modules: cat.total ?? cat.modules?.length ?? 0,
        active_peers: (Array.isArray(peers.peers) ? peers.peers : []).filter((p) => p.enabled)
          .length,
      };
    },
    staleTime: 30_000,
  });

  return (
    <div className='space-y-4'>
      <StatsGrid columns={2}>
        <StatCard label={t('registry.statModulesInCatalog')} value={healthData?.modules ?? 0} icon={Package} />
        <StatCard label={t('registry.statActivePeers')} value={healthData?.active_peers ?? 0} icon={Globe} />
      </StatsGrid>

      <Card>
        <CardHeader>
          <CardTitle icon={Upload}>{t('registry.publishLocalModuleTitle')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <p className='text-xs text-gray-500'>
            {t('registry.publishDescription')}
          </p>
          <div className='flex gap-2'>
            <input
              className='flex-1 bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-blue-500/50'
              value={moduleName}
              onChange={(e) => setModuleName(e.target.value)}
              placeholder={t('registry.moduleNamePlaceholder')}
            />
            <Button
              variant='primary'
              onClick={() => publishMut.mutate(moduleName)}
              loading={publishMut.isPending}
              disabled={!moduleName.trim()}
              icon={<Upload size={14} />}
            >
              {t('registry.publishButton')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────



function MeshTreeTab() {
  const { t } = useTranslation();
  const [panels, setPanels] = useState<{ ResourcesPanel?: ComponentType } | null>(null);
  const [panelsError, setPanelsError] = useState<string | null>(null);
  useEffect(() => {
    // mesh.tsx je jen ModuleWorkspace wrapper — panely jsou v mesh/panels.tsx
    import('../mesh/panels').then((mod) => {
      if (typeof mod.ResourcesPanel !== 'function') {
        throw new Error('mesh/panels: ResourcesPanel missing');
      }
      setPanels({ ResourcesPanel: mod.ResourcesPanel });
    }).catch((e: Error) => {
      console.error('registry: import(../mesh/panels) selhal', e);
      setPanelsError(e?.message ?? String(e));
    });
  }, []);
  if (panels?.ResourcesPanel) return <panels.ResourcesPanel />;
  if (panelsError) {
    return (
      <div className='text-red-400 py-8 text-center text-sm'>
        {t('registry.panelLoadFailed')} ({panelsError})
      </div>
    );
  }
  return <div className='text-gray-500 py-8 text-center'>{t('registry.loadingLabel')}</div>;
}

function MeshHealthTab() {
  const { t } = useTranslation();
  const [panels, setPanels] = useState<{ HealthPanel?: ComponentType } | null>(null);
  const [panelsError, setPanelsError] = useState<string | null>(null);
  useEffect(() => {
    import('../mesh/panels').then((mod) => {
      if (typeof mod.HealthPanel !== 'function') {
        throw new Error('mesh/panels: HealthPanel missing');
      }
      setPanels({ HealthPanel: mod.HealthPanel });
    }).catch((e: Error) => {
      console.error('registry: import(../mesh/panels) selhal', e);
      setPanelsError(e?.message ?? String(e));
    });
  }, []);
  if (panels?.HealthPanel) return <panels.HealthPanel />;
  if (panelsError) {
    return (
      <div className='text-red-400 py-8 text-center text-sm'>
        {t('registry.panelLoadFailed')} ({panelsError})
      </div>
    );
  }
  return <div className='text-gray-500 py-8 text-center'>{t('registry.loadingLabel')}</div>;
}

export function TreePanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  return <MeshTreeTab />;
}
export function RegistryHealthPanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  return <MeshHealthTab />;
}
export function CatalogPanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  return <CatalogTab />;
}
export function PeersPanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  return <PeersTab />;
}
export function RegistryPublishPanel({ scope }: WidgetRenderProps) {
  setWidgetScope(scope ?? { node: null, identity: null });
  return <PublishTab />;
}
